// De adressen van een wervingsactie als wijk › buurt › straat, met per niveau
// hoeveel er gekozen zijn en of dat leeg, deels of alles is.
//
// Nodig omdat het bestaande groupAddressesByStreet alleen straten kent. Om een
// hele buurt of wijk in één keer te kiezen moet elk niveau zijn eigen telling
// en stand hebben. De straten komen nog steeds uit groupAddressesByStreet, dus
// de volgorde binnen een buurt blijft dezelfde.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)

import { groupAddressesByStreet } from './groupAddressesByStreet'

const OVERIG = 'Overig'

/**
 * @typedef {'none' | 'some' | 'all'} SelectieStand
 */

/**
 * @typedef {object} StraatGroep
 * @property {string} naam - Straatnaam, of 'Overig'
 * @property {number} total - Aantal adressen
 * @property {number} selected - Aantal daarvan gekozen
 * @property {SelectieStand} state
 * @property {Array<object>} addresses - De adressen, op huisnummer
 */

/**
 * @typedef {object} BuurtGroep
 * @property {string} code - CBS-buurtcode; leeg voor 'Overig'
 * @property {string} naam - Buurtnaam; leeg zolang de namen nog niet geladen zijn
 * @property {number} total
 * @property {number} selected
 * @property {SelectieStand} state
 * @property {Array<StraatGroep>} straten
 */

/**
 * @typedef {object} WijkGroep
 * @property {string} code - CBS-wijkcode
 * @property {string} naam - Wijknaam uit de actie
 * @property {number} total
 * @property {number} selected
 * @property {SelectieStand} state
 * @property {Array<BuurtGroep>} buurten
 */

/**
 * @param {number} selected
 * @param {number} total
 * @returns {SelectieStand}
 */
const selectionState = (selected, total) => {
    if (selected === 0 || total === 0) return 'none'
    return selected >= total ? 'all' : 'some'
}

/**
 * @param {Array<{ Key: string }>} addresses
 * @param {Set<string>} selectedKeys
 * @returns {{ total: number, selected: number, state: SelectieStand }}
 */
const count = (addresses, selectedKeys) => {
    const selected = addresses.filter((a) => selectedKeys.has(a.Key)).length
    return { total: addresses.length, selected, state: selectionState(selected, addresses.length) }
}

/**
 * @param {Array<object>} addresses
 * @param {string} key - Veldnaam om op te groeperen
 * @returns {Map<string, Array<object>>}
 */
const groupBy = (addresses, key) => {
    const map = new Map()
    for (const a of addresses) {
        const value = a[key] || ''
        if (!map.has(value)) map.set(value, [])
        map.get(value).push(a)
    }
    return map
}

/**
 * Buurten op naam, of op code zolang de namen er niet zijn. 'Overig' onderaan.
 *
 * @param {BuurtGroep} a
 * @param {BuurtGroep} b
 * @returns {number}
 */
const compareBuurten = (a, b) => {
    if (!a.code !== !b.code) return a.code ? -1 : 1
    return (a.naam || a.code).localeCompare(b.naam || b.code, 'nl') || a.code.localeCompare(b.code)
}

/**
 * @param {object} args
 * @param {Array<object>} args.addresses - Alle adressen in de gekozen wijken (uit PDOK)
 * @param {Set<string>} args.selectedKeys - `Key` van elk adres met een levende rij
 * @param {Array<{ WijkCode: string, Naam: string }>} args.wijken - De gekozen wijken, in de volgorde van de actie
 * @param {Object<string, string>} args.buurtNames - Buurtcode → buurtnaam
 * @returns {Array<WijkGroep>}
 */
const groupSelectionState = ({ addresses, selectedKeys, wijken, buurtNames }) => {
    const perWijk = groupBy(addresses || [], 'WijkCode')

    return (wijken || []).map((wijk) => {
        const inWijk = perWijk.get(wijk.WijkCode) || []

        const buurten = Array.from(groupBy(inWijk, 'BuurtCode').entries())
            .map(([code, inBuurt]) => ({
                code,
                naam: code ? (buurtNames?.[code] || '') : OVERIG,
                ...count(inBuurt, selectedKeys),
                straten: groupAddressesByStreet(inBuurt).map(([naam, inStraat]) => ({
                    naam,
                    ...count(inStraat, selectedKeys),
                    addresses: inStraat,
                })),
            }))
            .sort(compareBuurten)

        return { code: wijk.WijkCode, naam: wijk.Naam, ...count(inWijk, selectedKeys), buurten }
    })
}

export { groupSelectionState, selectionState }
